import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, GripVertical, ArrowLeft } from 'lucide-react';
import { FormField, JobFormData } from './types';

interface ApplicationFormBuilderProps {
  formData: JobFormData;
  applicationFormFields: FormField[];
  setApplicationFormFields: React.Dispatch<React.SetStateAction<FormField[]>>;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  onCancel: () => void;
  loading: boolean;
  isEditing: boolean;
}

const ApplicationFormBuilder: React.FC<ApplicationFormBuilderProps> = ({
  formData,
  applicationFormFields,
  setApplicationFormFields,
  onSubmit,
  onBack,
  onCancel,
  loading,
  isEditing
}) => {
  const addFormField = () => {
    const newField: FormField = {
      id: `custom_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      order: applicationFormFields.length + 1
    };
    setApplicationFormFields([...applicationFormFields, newField]);
  };

  const updateFormField = (fieldId: string, updates: Partial<FormField>) => {
    setApplicationFormFields(prev => 
      prev.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );
  };

  const removeFormField = (fieldId: string) => {
    const coreFields = ['firstName', 'lastName', 'email'];
    if (coreFields.includes(fieldId)) {
      return; // Cannot remove core fields
    }
    
    setApplicationFormFields(prev => prev.filter(field => field.id !== fieldId));
  };

  const moveFormField = (fieldId: string, direction: 'up' | 'down') => {
    const fieldIndex = applicationFormFields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) return;
    
    const newFields = [...applicationFormFields];
    const targetIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    
    if (targetIndex >= 0 && targetIndex < newFields.length) {
      [newFields[fieldIndex], newFields[targetIndex]] = [newFields[targetIndex], newFields[fieldIndex]];
      
      newFields.forEach((field, index) => {
        field.order = index + 1;
      });
      
      setApplicationFormFields(newFields);
    }
  };

  return (
    <div className="flex gap-6 h-[70vh]">
      {/* Form Builder */}
      <div className="w-1/2 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Application Form Fields</h3>
          <Button onClick={addFormField} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Field
          </Button>
        </div>
        
        <ScrollArea className="h-[55vh]">
          <div className="space-y-3 pr-4">
            {applicationFormFields.map((field, index) => (
              <Card key={field.id} className="p-3">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                      <Input
                        value={field.label}
                        onChange={(e) => updateFormField(field.id, { label: e.target.value })}
                        className="text-sm"
                        placeholder="Field label"
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveFormField(field.id, 'up')}
                        disabled={index === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveFormField(field.id, 'down')}
                        disabled={index === applicationFormFields.length - 1}
                      >
                        ↓
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFormField(field.id)}
                        disabled={['firstName', 'lastName', 'email'].includes(field.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={field.type}
                      onValueChange={(value: unknown) => updateFormField(field.id, { type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="textarea">Textarea</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="select">Select</SelectItem>
                        <SelectItem value="checkbox">Checkbox</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={field.required}
                        onCheckedChange={(checked) => updateFormField(field.id, { required: checked })}
                      />
                      <Label className="text-sm">Required</Label>
                    </div>
                  </div>
                  
                  <Input
                    value={field.placeholder || ''}
                    onChange={(e) => updateFormField(field.id, { placeholder: e.target.value })}
                    placeholder="Placeholder text"
                    className="text-sm"
                  />
                </div>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Preview */}
      <div className="w-1/2 border-l pl-6">
        <h3 className="text-lg font-semibold mb-4">Form Preview</h3>
        <div className="space-y-4 overflow-y-auto max-h-[55vh] bg-muted/20 p-4 rounded-lg">
          <div className="text-sm text-muted-foreground mb-4">
            Application form for: <strong>{formData.title || 'Your Job Title'}</strong>
          </div>
          
          {applicationFormFields.map((field) => (
            <div key={field.id} className="space-y-2">
              <Label className="text-sm">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              
              {field.type === 'textarea' ? (
                <Textarea
                  placeholder={field.placeholder}
                  disabled
                  className="bg-background"
                  rows={3}
                />
              ) : field.type === 'select' ? (
                <Select disabled>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                </Select>
              ) : field.type === 'checkbox' ? (
                <div className="flex items-center space-x-2">
                  <input type="checkbox" disabled className="rounded" />
                  <span className="text-sm">{field.placeholder || 'Checkbox option'}</span>
                </div>
              ) : (
                <Input
                  type={field.type === 'phone' ? 'tel' : field.type}
                  placeholder={field.placeholder}
                  disabled
                  className="bg-background"
                />
              )}
            </div>
          ))}
          
          <Button disabled className="w-full mt-6">
            Submit Application
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFormBuilder;